import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, User, ArrowRight, Shield, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { GmedLogo } from '../components/Logo';
import { authAPI, setAuthToken } from '../services/api';

export const AdminLogin = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [adminExists, setAdminExists] = useState(true);
  // Read synchronously at mount: a toast emitted here would be lost after the hard redirect
  const [sessionExpired] = useState(() => {
    const expired = window.sessionStorage.getItem('gmed_session_expired') === '1';
    if (expired) window.sessionStorage.removeItem('gmed_session_expired');
    return expired;
  });
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check if already logged in
      const token = localStorage.getItem('gmed_admin_token');
      if (token) {
        try {
          await authAPI.me();
          navigate('/admin');
          return;
        } catch {
          // Token invalid, remove it
          setAuthToken(null);
        }
      }
      
      // Check if admin exists
      const response = await authAPI.check();
      setAdminExists(response.data.admin_exists);
      setIsLogin(response.data.admin_exists);
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      if (isLogin) {
        response = await authAPI.login({
          email: formData.email,
          password: formData.password
        });
      } else {
        response = await authAPI.register({
          email: formData.email,
          password: formData.password,
          name: formData.name
        });
      }

      setAuthToken(response.data.access_token);
      toast.success(isLogin ? 'Login effettuato!' : 'Account creato!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore di autenticazione');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center p-4" data-testid="admin-login-page">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <GmedLogo className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-display font-bold text-white">Admin Portal</h1>
        </div>

        <Card className="glass-card border-white/10">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-xl text-white">
              {!adminExists ? 'Crea Account Admin' : (isLogin ? 'Accedi' : 'Registrati')}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {!adminExists 
                ? 'Crea il primo account amministratore'
                : (isLogin ? 'Inserisci le tue credenziali' : 'Crea un nuovo account')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessionExpired && (
              <div
                className="mb-4 flex items-start gap-2 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-sm text-amber-200"
                data-testid="session-expired-notice"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Sessione scaduta: accedi di nuovo per salvare le modifiche.</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {(!isLogin || !adminExists) && (
                <div>
                  <Label htmlFor="name" className="text-gray-300">Nome</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={!isLogin}
                      placeholder="Nome Admin"
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      data-testid="admin-name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="admin@gmed.it"
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    placeholder="••••••••"
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full mt-6"
                disabled={loading}
                data-testid="admin-submit-btn"
              >
                {loading ? 'Caricamento...' : (isLogin ? 'Accedi' : 'Crea Account')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            {adminExists && (
              <p className="text-center text-sm text-gray-500 mt-6">
                {isLogin ? 'Non hai un account?' : 'Hai già un account?'}{' '}
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-primary hover:underline"
                >
                  {isLogin ? 'Registrati' : 'Accedi'}
                </button>
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
